<script setup lang="ts">
    import { computed, onMounted, ref, useSlots } from 'vue';
    import { LoaderCircleIcon } from '@lucide/vue';
    import { RouteLocationRaw, Router, RouterLink, useRouter } from 'vue-router';

    const props = withDefaults(
        defineProps<{
            disabled?: boolean;
            loading?: boolean;
            size?: 'small' | 'large';
            class?: string;
            type?: 'button' | 'submit' | 'reset';
            variant?:
                | 'primary'
                | 'secondary'
                | 'tertiary'
                | 'block'
                | 'chip'
                | 'chip-dashed'
                | 'dashed'
                | 'danger';
            href?: string;
            target?: '_self' | '_blank';
            route?: RouteLocationRaw;
            backOrFallback?: RouteLocationRaw;
        }>(),
        {
            disabled: false,
            loading: false,
            size: 'large',
            class: undefined,
            type: 'button',
            variant: 'secondary',
            href: undefined,
            target: '_blank',
            route: undefined,
            backOrFallback: undefined,
        }
    );

    const slots = useSlots();
    const hasIconLeft = computed(() => !!slots['icon-left']);
    const hasIconRight = computed(() => !!slots['icon-right']);

    const isDisabled = computed(() => props.disabled || props.loading);
    const loadingRight = computed(
        () => props.loading && (!hasIconLeft.value || (hasIconRight.value && hasIconLeft.value))
    );
    const loadingLeft = computed(() => props.loading && !hasIconRight.value);
    const isButton = computed(() => !props.route && !props.href);
    const isRouter = computed(() => !!props.route);
    const isExternal = computed(() => !!props.href && /^(https?:|mailto:|tel:)/i.test(props.href!));

    const router = ref<Router | null>(null);

    const buttonClasses = computed(() => {
        return [
            'Button',
            `Button--${props.size}`,
            `Button--${props.variant}`,
            props.loading ? 'Button--loading' : '',
            hasIconLeft.value ? 'Button--with-start-icon' : '',
            hasIconRight.value || (isButton.value && props.loading) ? 'Button--with-end-icon' : '',
        ];
    });

    onMounted(async () => {
        if (props.class) {
            console.error(
                'The class prop is disabled for EchoButton. Please use variants and size props instead.',
                props.class
            );
        }

        if (props.backOrFallback) {
            router.value = useRouter();
            await router.value?.isReady();
        }
    });

    type AttributeBindings = {
        'to': RouteLocationRaw;
        'href': string | undefined;
        'class': string | undefined;
        'aria-disabled': boolean | undefined;
        'tabindex': number | undefined;
        'disabled': boolean;
        'type': 'button' | 'submit' | 'reset';
    };

    const attributes = computed(() => {
        let bindings = {} as AttributeBindings;

        if (isRouter.value) {
            bindings.to = props.route || '';
        } else if (isExternal.value) {
            bindings.href = props.href;
        }

        if (isRouter.value || isExternal.value) {
            bindings['aria-disabled'] = isDisabled.value || undefined;
        } else {
            bindings.disabled = isDisabled.value;
            bindings.type = props.type;
        }

        bindings.tabindex = isDisabled.value ? -1 : undefined;
        bindings.class = buttonClasses.value.join(' ');

        return bindings;
    });

    const handleClick = () => {
        if (isDisabled.value) {
            return;
        }

        if (props.backOrFallback) {
            window.history.length > 1
                ? router.value?.back()
                : router.value?.push(props.backOrFallback);
        }
    };
</script>

<template>
    <RouterLink v-if="isRouter" v-bind="attributes">
        <span v-if="hasIconLeft" class="Button__icon" aria-hidden="true">
            <slot name="icon-left" />
        </span>
        <slot />
        <span v-if="hasIconRight" class="Button__icon" aria-hidden="true">
            <slot name="icon-right" />
        </span>
    </RouterLink>

    <a
        v-else-if="isExternal"
        v-bind="attributes"
        :target="target"
        :rel="target === '_blank' ? 'noopener noreferrer' : undefined"
    >
        <span v-if="hasIconLeft" class="Button__icon" aria-hidden="true">
            <slot name="icon-left" />
        </span>
        <slot />
        <span v-if="hasIconRight" class="Button__icon" aria-hidden="true">
            <slot name="icon-right" />
        </span>
    </a>

    <button v-else v-bind="attributes" @click="handleClick()">
        <span v-if="hasIconLeft" class="Button__icon" aria-hidden="true">
            <span v-show="!loadingLeft" class="block size-full">
                <slot name="icon-left" />
            </span>
            <LoaderCircleIcon
                class="size-full"
                :class="{ 'animate-spin block': loadingLeft, 'hidden': !loadingLeft }"
            />
        </span>
        <slot />
        <span v-if="hasIconRight || loadingRight" class="Button__icon" aria-hidden="true">
            <span v-show="!loadingRight" class="block size-full">
                <slot name="icon-right" />
            </span>
            <LoaderCircleIcon
                v-if="loading"
                class="size-full"
                :class="{ 'animate-spin block': loadingRight, 'hidden': !loadingRight }"
            />
        </span>
    </button>
</template>
